import { Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  useStripeMigration,
  useStripeMigrationAction,
} from '@/lib/services/stripe-connect/stripe-migration.query'
import { stripeUpgradeLink } from '@/lib/stripe-financial-routes'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { getErrorMessage } from '@/lib/utils'
import StripeMigrationStatusNotice from './StripeMigrationStatusNotice'
import StripeMigrationProgressNotice from './StripeMigrationProgressNotice'

export default function StripeMigrationInvitation({ organizationId }) {
  const navigate = useNavigate()
  const { data } = useStripeMigration(organizationId)
  const start = useStripeMigrationAction(organizationId, 'start')
  const openOnboarding = async () => {
    try {
      await start.mutateAsync()
      await navigate(stripeUpgradeLink(organizationId))
    } catch (error) {
      toast.error('Could not open Stripe setup', { description: getErrorMessage(error) })
    }
  }
  if (data?.ownership_blocked) return <StripeMigrationStatusNotice migration={data} />
  if (!data?.available && !data?.started) return null
  const action = (
    <Button size="sm" variant="outline" asChild>
      <Link {...stripeUpgradeLink(organizationId)}>
        {data.activated
          ? 'View account upgrade'
          : data.target_disconnected
            ? 'View upgrade status'
            : data.onboarding_complete
              ? data.requires_action
                ? 'Review Stripe requirements'
                : 'View upgrade status'
              : 'Continue setup'}
      </Link>
    </Button>
  )
  if (data.started && !data.onboarding_complete && !data.activated && !data.target_disconnected) {
    return (
      <StripeMigrationStatusNotice
        migration={data}
        action={action}
        fallback={<InfoNotice title="Finish your Stripe account upgrade" action={action} />}
      />
    )
  }
  if (data.started && !data.activated)
    return (
      <StripeMigrationProgressNotice
        migration={data}
        action={action}
        requiresAction={data.requires_action}
      />
    )
  if (data.started) return <StripeMigrationStatusNotice migration={data} action={action} />
  return (
    <InfoNotice
      title="Upgrade your Stripe account"
      action={
        <Button size="sm" variant="outline" disabled={start.isPending} onClick={openOnboarding}>
          {start.isPending ? 'Opening Stripe…' : 'Upgrade account'}
        </Button>
      }
    >
      Get access to new features as they become available. You can keep accepting payments while you
      complete setup.
    </InfoNotice>
  )
}
